import { useModules } from 'src/context/moduleContext';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

export const useGetRibbonItemVisibilities = () => {
  const { isModuleEnabled } = useModules();
  const modulesEnabled = useIsFeatureVisibleToOrg('modules');

  // Call all hooks unconditionally to satisfy React Hooks rules
  // these feature flag checks will be removed once modules are fully rolled out
  const approversFeatureFlag = useIsFeatureVisibleToOrg('approvers');
  const attestationsFeatureFlag = useIsFeatureVisibleToOrg('attestations');
  const wizardFeatureFlag = useIsFeatureVisibleToOrg('wizard');
  const policyFeatureFlag = useIsFeatureVisibleToOrg('policy');
  const complianceFeatureFlag = useIsFeatureVisibleToOrg('compliance');

  const approvalsEnabled = modulesEnabled
    ? isModuleEnabled('approval')
    : approversFeatureFlag;
  const attestationEnabled = modulesEnabled
    ? isModuleEnabled('document.subModules.attestation')
    : attestationsFeatureFlag;

  const actionEnabled = modulesEnabled ? isModuleEnabled('action') : true;
  const riskEnabled = modulesEnabled ? isModuleEnabled('risk') : true;
  const rcsaEnabled = modulesEnabled
    ? isModuleEnabled('risk.subModules.rcsa_wizard')
    : wizardFeatureFlag;

  const indicatorEnabled = modulesEnabled ? isModuleEnabled('indicator') : true;

  const policyEnabled = modulesEnabled
    ? isModuleEnabled('document')
    : policyFeatureFlag;

  const assessmentEnabled = modulesEnabled
    ? isModuleEnabled('assessment')
    : true;

  const controlEnabled = modulesEnabled ? isModuleEnabled('control') : true;

  const issueEnabled = modulesEnabled ? isModuleEnabled('issue') : true;

  const complianceEnabled = modulesEnabled
    ? isModuleEnabled('obligation')
    : complianceFeatureFlag;

  const { hasPermission: hasApprovalPermission, loading: isLoadingApproval } =
    useHasPermissionQuery('read:change_request', undefined, true);

  const { hasPermission: hasActionPermission, loading: isLoadingAction } =
    useHasPermissionQuery('read:action', undefined, true);
  const { hasPermission: hasRiskPermission, loading: isLoadingRisk } =
    useHasPermissionQuery('read:risk', undefined, true);
  const {
    hasPermission: hasAssessmentPermission,
    loading: isLoadingAssessment,
  } = useHasPermissionQuery('read:assessment', undefined, true);
  const { hasPermission: hasIndicatorPermission, loading: isLoadingIndicator } =
    useHasPermissionQuery('read:indicator', undefined, true);
  const { hasPermission: hasPolicyPermission, loading: isLoadingPolicy } =
    useHasPermissionQuery('read:document', undefined, true);
  const { hasPermission: hasControlPermission, loading: isLoadingControl } =
    useHasPermissionQuery('read:control', undefined, true);
  const { hasPermission: hasIssuePermission, loading: isLoadingIssue } =
    useHasPermissionQuery('read:issue', undefined, true);
  const {
    hasPermission: hasObligationPermission,
    loading: isLoadingObligation,
  } = useHasPermissionQuery('read:obligation', undefined, true);

  const isLoading =
    isLoadingApproval ||
    isLoadingAction ||
    isLoadingRisk ||
    isLoadingAssessment ||
    isLoadingIndicator ||
    isLoadingPolicy ||
    isLoadingControl ||
    isLoadingIssue ||
    isLoadingObligation;

  if (isLoading) {
    return {
      approvals: false,
      attestations: false,
      actions: false,
      risks: false,
      rcsa: false,
      indicators: false,
      policies: false,
      assessments: false,
      controls: false,
      issues: false,
      obligations: false,
    };
  }

  return {
    approvals: hasApprovalPermission && approvalsEnabled,
    attestations: attestationEnabled,
    actions: hasActionPermission && actionEnabled,
    risks: hasRiskPermission && riskEnabled,
    rcsa: hasAssessmentPermission && rcsaEnabled,
    indicators: hasIndicatorPermission && indicatorEnabled,
    policies: hasPolicyPermission && policyEnabled,
    assessments: hasAssessmentPermission && assessmentEnabled,
    controls: hasControlPermission && controlEnabled,
    issues: hasIssuePermission && issueEnabled,
    obligations: hasObligationPermission && complianceEnabled,
  };
};
